import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Code,
  Divider,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { joinFromColumns, splitForColumns, type LocalizedString } from '../api/localized-string';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';
import {
  useArchiveQuestionSet,
  useNewVersionQuestionSet,
  usePublishQuestionSet,
  useQuestionSet,
  useUpdateQuestionSet,
  type QuestionSetStatus,
} from '../api/question-sets';
import type { QuestionSpec } from '../api/workflows';
import { LocalizedStringInput } from '../components/localized-string-input';
import { QuestionListEditor } from '../components/question-editor';

const STATUS_COLOURS: Record<QuestionSetStatus, string> = {
  draft: 'yellow',
  active: 'green',
  archived: 'gray',
};

/**
 * Serialize the editable fields so we can cheaply detect whether the
 * editor has drifted from the last-saved state. Stable because every
 * caller builds the inputs the same way (no key-order churn).
 */
function snapshotOf(
  name: LocalizedString,
  description: LocalizedString,
  questions: QuestionSpec[],
): string {
  return JSON.stringify({ name, description, questions });
}

/**
 * Edit / inspect a question set. Drafts are fully editable (rename,
 * description, questions, plus publish + archive actions). Active +
 * archived rows are read-only — the operator must fork a new draft via
 * "New version" to change them, mirroring the workflow-definition UX.
 */
export function QuestionSetEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useQuestionSet(id);
  const orgs = useOrgs();
  const products = useProducts();
  const update = useUpdateQuestionSet(id ?? '');
  const publish = usePublishQuestionSet(id ?? '');
  const archive = useArchiveQuestionSet(id ?? '');
  const newVersion = useNewVersionQuestionSet(id ?? '');

  const set = query.data;
  const isDraft = set?.status === 'draft';
  const orgName = set
    ? set.orgId === null
      ? 'Default — all orgs'
      : (orgs.data?.find((o) => o.id === set.orgId)?.name ?? set.orgId)
    : '';
  const productName = set
    ? (products.data?.find((p) => p.id === set.productId)?.displayName ?? set.productId)
    : '';

  // Local form state seeded from the loaded row; the "Save" button
  // PATCH'es with whatever's dirty. Same pattern as the workflow editor.
  //
  // `name` / `description` here are LocalizedString objects spanning
  // the default English text + sidecar translations. The API serves
  // them as two separate fields (`name` + `nameTranslations`) and we
  // re-join on load / re-split on save.
  const [name, setName] = useState<LocalizedString>({ default: '' });
  const [description, setDescription] = useState<LocalizedString>({ default: '' });
  const [questions, setQuestions] = useState<QuestionSpec[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Serialized copy of the last-persisted editor state. Publishing is
  // gated on this matching the current state so we never promote a draft
  // whose on-screen edits haven't been saved (the backend publish takes
  // no body — it promotes whatever was last persisted).
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');

  useEffect(() => {
    if (!set) return;
    const loadedName = joinFromColumns(set.name, set.nameTranslations);
    const loadedDescription = joinFromColumns(set.description ?? '', set.descriptionTranslations);
    setName(loadedName);
    setDescription(loadedDescription);
    setQuestions(set.questions);
    setSavedSnapshot(snapshotOf(loadedName, loadedDescription, set.questions));
  }, [set]);

  const isDirty = isDraft && snapshotOf(name, description, questions) !== savedSnapshot;

  if (query.isPending) {
    return (
      <Center mih={200}>
        <Text c="dimmed">Loading…</Text>
      </Center>
    );
  }

  if (query.isError || !set) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/question-sets" size="sm">
          ← Back to question sets
        </Anchor>
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Could not load question set.
        </Alert>
      </Stack>
    );
  }

  function errorMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      const parsed = (() => {
        try {
          return JSON.parse(err.body) as { message?: string };
        } catch {
          return null;
        }
      })();
      return parsed?.message ?? `${fallback} (${err.status})`;
    }
    return err instanceof Error ? err.message : fallback;
  }

  /** Build the PATCH payload from the current editor state. */
  function currentUpdate() {
    const nameSplit = splitForColumns(name);
    const descSplit = splitForColumns(description);
    return {
      name: nameSplit.default.trim(),
      description: descSplit.default.trim() || undefined,
      nameTranslations: nameSplit.translations,
      descriptionTranslations: descSplit.translations,
      questions,
    };
  }

  function handleSave() {
    if (!isDraft) return;
    setError(null);
    update.mutate(currentUpdate(), {
      onSuccess: () => {
        // Editor state is now persisted — clear the dirty flag so Publish
        // unlocks (see `isDirty`).
        setSavedSnapshot(snapshotOf(name, description, questions));
        notifications.show({ color: 'green', message: 'Question set saved.' });
      },
      onError: (err) => setError(errorMessage(err, 'Save failed')),
    });
  }

  // Publish is a pure state transition: it flips the persisted draft to
  // active and takes no body. The button is disabled while `isDirty`, so
  // by the time we get here the on-screen edits are already saved and
  // can't be silently dropped.
  function handlePublish() {
    if (!isDraft || isDirty) return;
    setError(null);
    publish.mutate(undefined, {
      onSuccess: () => notifications.show({ color: 'green', message: 'Question set published.' }),
      onError: (err) => setError(errorMessage(err, 'Publish failed')),
    });
  }

  function handleArchive() {
    archive.mutate(undefined, {
      onSuccess: () => notifications.show({ color: 'green', message: 'Question set archived.' }),
    });
  }

  function handleNewVersion() {
    newVersion.mutate(undefined, {
      onSuccess: (forked) => {
        notifications.show({ color: 'green', message: `New draft @ v${forked.version}.` });
        navigate(`/question-sets/${forked.id}`);
      },
    });
  }

  return (
    <Stack gap="lg" maw={900}>
      <Anchor component={Link} to="/question-sets" size="sm">
        ← Back to question sets
      </Anchor>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={2}>{set.name}</Title>
            <Badge color={STATUS_COLOURS[set.status]} variant="light">
              {set.status}
            </Badge>
            <Text size="sm" c="dimmed">
              v{set.version}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            <Code>{orgName}</Code> / <Code>{productName}</Code>
          </Text>
        </Stack>
        <Group>
          {isDraft && (
            <>
              {isDirty && (
                <Badge color="yellow" variant="light">
                  Unsaved changes
                </Badge>
              )}
              <Button
                variant="default"
                loading={update.isPending}
                disabled={!isDirty}
                onClick={handleSave}
              >
                Save draft
              </Button>
              <Button
                color="green"
                loading={publish.isPending}
                disabled={isDirty}
                onClick={handlePublish}
              >
                Publish
              </Button>
            </>
          )}
          {!isDraft && (
            <Button variant="default" loading={newVersion.isPending} onClick={handleNewVersion}>
              New version
            </Button>
          )}
          {set.status === 'active' && (
            <Button
              color="red"
              variant="subtle"
              loading={archive.isPending}
              onClick={handleArchive}
            >
              Archive
            </Button>
          )}
        </Group>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      {!isDraft && (
        <Alert color="gray" icon={<IconAlertTriangle size={16} />}>
          This {set.status} version is read-only. Click{' '}
          <Text component="span" fw={600}>
            New version
          </Text>{' '}
          above to fork a fresh editable draft under the same <Code>{set.name}</Code> name.
        </Alert>
      )}

      <Divider label="Details" labelPosition="left" />
      <LocalizedStringInput label="Name" value={name} onChange={setName} disabled={!isDraft} />
      <LocalizedStringInput
        label="Description"
        value={description}
        onChange={setDescription}
        disabled={!isDraft}
        multiline
      />

      <Divider label={`Questions (${questions.length})`} labelPosition="left" />
      {isDraft ? (
        <QuestionListEditor questions={questions} onChange={setQuestions} />
      ) : (
        <Stack gap="md">
          {questions.map((q) => (
            <Stack
              key={q.id}
              gap={2}
              p="sm"
              style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 }}
            >
              <Text size="xs" c="dimmed">
                <Code>{q.id}</Code> · {q.answer.type} {q.required ? '· required' : ''}
              </Text>
              <Text size="sm">{typeof q.prompt === 'string' ? q.prompt : q.prompt.default}</Text>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
